import type { QueryKey } from "react-query";

/**
 * The durable store behind the tenant query cache. One snapshot per tenant scope
 * (`TenantQueryClientProvider`'s `organization--apiBase--member`), written and read as
 * one string. A host that has a faster store than IndexedDB publishes one as
 * `Platform.queryCache` (the desktop: the Rust page cache, which the packaged app
 * keeps under its own data directory); the browser hosts fall back to IndexedDB inside
 * the provider. The store never sees a query key, only whole snapshots, so tenant
 * isolation is the scope string alone: every read and write names it.
 */
export interface QueryCacheStore {
    read(scope: string): Promise<string | null>;
    write(scope: string, snapshot: string): Promise<void>;
    remove(scope: string): Promise<void>;
}

/** How long a persisted query counts as fresh after hydration: the provider's own
 *  default `staleTime`. A screen that paints from the snapshot refetches only past it. */
export const PERSISTED_QUERY_STALE_TIME = 5 * 60 * 1000;

/** The part of a persisted query the stale-empty rule reads and writes. */
export interface PersistedQueryState {
    state: { data: unknown; dataUpdatedAt: number };
}

/**
 * True for a persisted answer that lists nothing: an empty array, or a plain object
 * with at least one array-valued property where every array is empty and every other
 * property says "none" (`0`, `null`, `false`, `""`) -- `[]`, the folder list
 * `{ folders: [], total_document_count: 0 }`, a page `{ items: [], total: 0 }`. An
 * object that pairs an empty array with a real count (`{ folders: [],
 * total_document_count: 5 }`: no folders, documents at the root) still says something.
 */
export function isEmptyListAnswer(data: unknown): boolean {
    if (Array.isArray(data)) return data.length === 0;
    if (typeof data !== "object" || data === null) return false;
    let arrays = 0;
    for (const value of Object.values(data as Record<string, unknown>)) {
        if (Array.isArray(value)) {
            if (value.length > 0) return false;
            arrays += 1;
        } else if (!(
            value === 0 ||
            value === null ||
            value === undefined ||
            value === false ||
            value === ""
        )) {
            return false;
        }
    }
    return arrays > 0;
}

/**
 * The rule for an empty persisted list: a hint, never an answer, unless it is younger
 * than `PERSISTED_QUERY_STALE_TIME` (a list fetched in this session is never persisted
 * state to begin with). A stale empty list hydrates with `dataUpdatedAt` 0: an observer
 * that mounts on it reports `loading` and fetches (react-query v3
 * `queryObserver.createResult`), so the screen paints its skeleton -- not the empty
 * state -- until the network answers, while `getQueryData` readers (Home's cached
 * counts) still see the list. A stale non-empty list keeps its stamp: its rows paint
 * at once and the same fetch revalidates them behind the paint. A fresh empty list is
 * an answer and paints the empty state without a request. Mutates in place; the
 * caller hydrates the result.
 */
export function hintStaleEmptyAnswers(queries: readonly PersistedQueryState[], now: number): void {
    for (const query of queries) {
        const { state } = query;
        if (
            now - state.dataUpdatedAt > PERSISTED_QUERY_STALE_TIME &&
            isEmptyListAnswer(state.data)
        ) {
            state.dataUpdatedAt = 0;
        }
    }
}

const persistedPrefixes: (readonly unknown[])[] = [];
const browserPrefixes: (readonly unknown[])[] = [];
/** Prefixes whose one answer may exceed the store's per-query limit, and by how much. */
const byteAllowances: { prefix: readonly unknown[]; bytes: number }[] = [];
const dependencies: { prefix: readonly unknown[]; sources: readonly (readonly unknown[])[] }[] = [];
/** Keys under a registered prefix that a screen asked to keep out of the store. */
const persistedExceptions: ((queryKey: readonly unknown[]) => boolean)[] = [];

/**
 * Registers query-key prefixes whose successful data persists to a host's own store
 * (`Platform.queryCache`, the desktop) and hydrates before the tenant subtree's first
 * render there. A screen's `register.ts` calls this at module level (the boot imports
 * every register file before the providers mount), naming the prefixes of the queries
 * its first paint needs: `["conversations"]` matches the bare string key
 * `"conversations"` as well as `["conversations", ...]`. Registration is additive and
 * idempotent. `browser` opts compact startup reads into the browser's bounded
 * IndexedDB snapshot too. Large collections remain host-only by default.
 * The provider's role-gated exclusions (`shouldPersistQuery`) still apply to a
 * registered key. `except` keeps named keys under a registered prefix out of the store
 * (a record's history pages, which carry a record snapshot per event); an exception
 * applies to every registration, so a screen that shares the prefix shares the rule.
 */
export function persistQueryKeys(
    prefixes: readonly (readonly unknown[])[],
    options: {
        except?: (queryKey: readonly unknown[]) => boolean;
        browser?: boolean;
        /** Changes/removal of these query families retire a derived snapshot. */
        invalidateOn?: readonly (readonly unknown[])[];
        /**
         * The most one answer under this prefix may take in the store, above the store's
         * per-query limit (the browser keeps 256 KB a query): a screen's whole index that its
         * first paint reads (Drawings' sheet index). The store's total grows by the same
         * amount, so the allowance never crowds out the other startup reads.
         */
        maxBytes?: number;
    } = {},
): void {
    for (const prefix of prefixes) {
        if (prefix.length === 0) continue;
        const known = persistedPrefixes.some(
            (candidate) =>
                candidate.length === prefix.length &&
                candidate.every((segment, index) => segment === prefix[index]),
        );
        if (!known) persistedPrefixes.push([...prefix]);
        if (options.browser && !browserPrefixes.some((candidate) => samePrefix(candidate, prefix)))
            browserPrefixes.push([...prefix]);
        if (options.maxBytes && !byteAllowances.some((entry) => samePrefix(entry.prefix, prefix)))
            byteAllowances.push({ prefix: [...prefix], bytes: options.maxBytes });
        if (options.invalidateOn) {
            const entry = dependencies.find((candidate) => samePrefix(candidate.prefix, prefix));
            const sources = [...(entry?.sources ?? [])];
            for (const source of options.invalidateOn)
                if (source.length && !sources.some((candidate) => samePrefix(candidate, source)))
                    sources.push([...source]);
            if (entry) entry.sources = sources;
            else dependencies.push({ prefix: [...prefix], sources });
        }
    }
    if (options.except) persistedExceptions.push(options.except);
}

function samePrefix(left: readonly unknown[], right: readonly unknown[]): boolean {
    return left.length === right.length && left.every((segment, index) => segment === right[index]);
}

function startsWith(key: readonly unknown[], prefix: readonly unknown[]): boolean {
    return prefix.length <= key.length && prefix.every((segment, index) => segment === key[index]);
}

/** True for a registered prefix on this host, unless a registration excepted it. */
export function isPersistedQueryKey(
    queryKey: QueryKey,
    host: "host" | "browser" = "host",
): boolean {
    const segments: readonly unknown[] = Array.isArray(queryKey) ? queryKey : [queryKey];
    return (
        (host === "browser" ? browserPrefixes : persistedPrefixes).some((prefix) =>
            startsWith(segments, prefix),
        ) && !persistedExceptions.some((excepted) => excepted(segments))
    );
}

/** The bytes one answer of `queryKey` may take in the store, beyond the store's own
 *  per-query limit: its registration's `maxBytes`, else 0. */
export function persistedByteAllowance(queryKey: QueryKey): number {
    const segments: readonly unknown[] = Array.isArray(queryKey) ? queryKey : [queryKey];
    return byteAllowances.reduce(
        (most, entry) => (startsWith(segments, entry.prefix) ? Math.max(most, entry.bytes) : most),
        0,
    );
}

/** What every allowance adds to a store's total budget. */
export function persistedAllowanceBytes(): number {
    return byteAllowances.reduce((total, entry) => total + entry.bytes, 0);
}

export function persistedDependencies(queryKey: QueryKey): readonly (readonly unknown[])[] {
    const segments = Array.isArray(queryKey) ? queryKey : [queryKey];
    return dependencies
        .filter((entry) => startsWith(segments, entry.prefix))
        .flatMap((entry) => entry.sources);
}

/** Dependents are removed, rather than merely invalidated: a partial display
 * snapshot has no fetcher and must never outlive a changed authoritative read. */
export function persistedDependents(queryKey: QueryKey): readonly (readonly unknown[])[] {
    const segments = Array.isArray(queryKey) ? queryKey : [queryKey];
    return dependencies
        .filter((entry) => entry.sources.some((source) => startsWith(segments, source)))
        .map((entry) => entry.prefix);
}

/** Test-only: forgets every registration so one test's prefixes never leak into the next. */
export function resetPersistedQueryKeysForTests(): void {
    persistedPrefixes.length = 0;
    browserPrefixes.length = 0;
    byteAllowances.length = 0;
    dependencies.length = 0;
    persistedExceptions.length = 0;
}
